import React, { useRef, useState, useCallback } from 'react';
import { cn } from '@express-labs/raven-tools';
import Placeholder from './subcomponents/Placeholder/Placeholder';
import FallbackImage from './subcomponents/FallbackImage/FallbackImage';
import { PlaceholderProps } from './subcomponents/Placeholder/Placeholder';
import { FallbackImageProps } from './subcomponents/FallbackImage/FallbackImage';
import s from './lazyImage.scss';

export type LazyImageProps = {
  alt: string;
  className?: string;
  customFallbackImage?: React.FC<FallbackImageProps>;
  customPlaceholder?: React.FC<PlaceholderProps>;
  height?: number;
  isFallbackImageEnabled?: boolean;
  isPlaceholderEnabled?: boolean;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onImageClick?: () => void;
  onIntersected?: (event: IntersectionObserverEntry) => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  src: string;
  width?: number;
};
const LAZY_IMG_LOADING = 'LAZY_IMG_LOADING';
const LAZY_IMG_LOADED = 'LAZY_IMG_LOADED';
const LAZY_IMG_ERROR = 'LAZY_IMG_ERROR';

export function getPlaceholder(customPlaceholder?: React.FC<PlaceholderProps>) {
  if (customPlaceholder) {
    const CustomPlaceholder = customPlaceholder;
    return <CustomPlaceholder />;
  }
  return <Placeholder />;
}

export function getFallbackImage(
  alt: string,
  customFallbackImage?: React.FC<FallbackImageProps>
) {
  if (customFallbackImage) {
    const CustomFallbackImage = customFallbackImage;
    return <CustomFallbackImage alt={alt} />;
  }
  return <FallbackImage alt={alt} />;
}

export function getClassNameByState(
  state: string,
  isFallbackImageEnabled: boolean
) {
  if (
    state === LAZY_IMG_LOADED ||
    (state === LAZY_IMG_ERROR && !isFallbackImageEnabled)
  ) {
    return 'loaded';
  }
  if (state === LAZY_IMG_ERROR && isFallbackImageEnabled) {
    return 'error';
  }
  return 'loading';
}

/**
 * @deprecated Please note, the HTML image tag now has a "loading" attribute that can be set to "lazy" or "eager" (the default is eager)
 */
function LazyImage({
  alt,
  className,
  customFallbackImage,
  customPlaceholder,
  height,
  isFallbackImageEnabled = true,
  isPlaceholderEnabled = true,
  onError,
  onImageClick,
  onIntersected,
  onLoad,
  src,
  width,
  ...props
}: LazyImageProps): React.ReactElement {
  const ref = useRef<HTMLImageElement | null>(null);
  const isPlaceholderEnabledRef = useRef<boolean>(isPlaceholderEnabled);
  const isFallbackImageEnabledRef = useRef<boolean>(isFallbackImageEnabled);
  const [state, setState] = useState<string>(LAZY_IMG_LOADING);
  const [stateClassName, setStateClassName] = useState<string>(
    getClassNameByState(state, isFallbackImageEnabledRef.current)
  );

  const onLoadHandler = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (state === LAZY_IMG_LOADING) {
        setState(LAZY_IMG_LOADED);
        setStateClassName(
          getClassNameByState(
            LAZY_IMG_LOADED,
            isFallbackImageEnabledRef.current
          )
        );
        if (typeof onLoad === 'function') {
          onLoad(e);
        }
      }
    },
    [state, isFallbackImageEnabledRef, onLoad]
  );

  const onErrorHandler = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (state !== LAZY_IMG_ERROR) {
        setState(LAZY_IMG_ERROR);
        setStateClassName(
          getClassNameByState(LAZY_IMG_ERROR, isFallbackImageEnabledRef.current)
        );
        if (typeof onError === 'function') {
          onError(e);
        }
      }
    },
    [isFallbackImageEnabledRef, onError]
  );

  const onClickHandler = useCallback(
    (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
      ev.preventDefault();
      if (typeof onImageClick === 'function') {
        onImageClick();
      }
    },
    [onImageClick]
  );

  function shouldRenderPlaceholder() {
    return isPlaceholderEnabledRef.current && state === LAZY_IMG_LOADING;
  }

  function shouldRenderFallbackImage() {
    return isFallbackImageEnabledRef.current && state === LAZY_IMG_ERROR;
  }

  if (onImageClick) {
    return (
      <button onClick={onClickHandler} className={s.imageContainer}>
        {shouldRenderPlaceholder() && getPlaceholder(customPlaceholder)}
        {shouldRenderFallbackImage() &&
          getFallbackImage(alt, customFallbackImage)}
        <img
          ref={ref}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          className={cn(s.image, s[stateClassName], className)}
          onLoad={onLoadHandler}
          onError={onErrorHandler}
          src={src}
          {...props}
        />
      </button>
    );
  }

  return (
    <>
      {shouldRenderPlaceholder() && getPlaceholder(customPlaceholder)}
      {shouldRenderFallbackImage() &&
        getFallbackImage(alt, customFallbackImage)}
      <img
        ref={ref}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        className={cn(s.image, s[stateClassName], className)}
        onLoad={onLoadHandler}
        onError={onErrorHandler}
        src={src}
        {...props}
      />
    </>
  );
}

LazyImage.displayName = 'LazyImage';

export default LazyImage;
